import { useEffect, useLayoutEffect, useCallback, useState, useContext, useRef } from 'react'
import { DropdownItem, UncontrolledDropdown, DropdownToggle, Spinner } from 'reactstrap'
import { FixedSizeList as List } from 'react-window'
import InfiniteLoader from 'react-window-infinite-loader'
import {
  IconSearch,
  IconTrash,
  IconEdit,
  IconDotsVertical,
  IconChevronRight,
  IconDots,
  IconMessagePlus,
  IconLayoutSidebarLeftCollapse,
  IconStack2,
  IconSquarePlus,
  IconChevronDown,
  IconChevronUp
} from '@tabler/icons-react'
import DeleteChatModal from './modals/DeleteChatModal'
import RenameChatModal from './modals/RenameChatModal'
import ChatSearchModal from './modals/ChatSearchModal'
import DeleteProjectModal from './modals/DeleteProjectModal'
import { useDispatch, useSelector } from 'react-redux'
import { useChatService } from './services/chatService'
import { toast } from 'react-toastify'
import { ChatbotContext } from './context/ChatbotProvider'
import { checkIsDrawerMode } from './utils/helpers'
import { extractOriginalDataSourceIds } from './utils/mcpServerUtils'
import { useTrackEvent } from '@microsoft/applicationinsights-react-js'
import { appInsights } from '../../../_services/AppInsights'
import { EventType } from '../../../utility/types'
import { useAppsService } from '../../../_services/AppsService'
import { Tooltip } from 'react-tooltip'
import { CHAT_MSG_PER_PAGE, MCP_SERVER_NAMES, ToolConfigMode } from './utils/constants'
import {
  isTempMessageId,
  normalizeInterruptedBranches,
  isAbortedContent,
  getMessageContent
} from './utils/TextUtils'
import { AgentData, ChatRawMessageItem } from './utils/types'
import { useNavigate, useParams } from 'react-router-dom'
import { setSelectedAgent } from '../../../_redux/actions/chatbotActions'
import { staticAssetsPath } from '../../../configs/constants'
import { useAgentService } from './agent/agentService'
import { useSasToken } from '../../../_hooks/useSasToken'
//@ts-ignore
import { Avatar, Divider } from 'dsm-react-lib'
import {
  ProjectFormModal,
  useProjectService,
  getProjectIcon,
  getProjectColour,
  type ProjectFormValue
} from './project'
import { DropdownMenuContainer } from '../../../styles/common'
import { useFeatureGate } from '../../../_hooks/useFeatureGate'
import { getInitials } from '@utils'

// Persisted outside component state (and mirrored to sessionStorage) so the sidebar's
// project-list scroll position survives a ChatHistory remount (e.g. navigating between
// /chat/project/:id and /chat/project/:id/:chatId) AND a full browser page reload, which
// re-runs this module and would otherwise reset the in-memory variable back to 0.
const PROJECTS_SCROLL_STORAGE_KEY = 'chatHistory:projectsScrollTop'

const readSavedProjectsScrollTop = (): number => {
  try {
    const stored = window.sessionStorage.getItem(PROJECTS_SCROLL_STORAGE_KEY)
    return stored ? Number(stored) || 0 : 0
  } catch {
    return 0
  }
}

let savedProjectsScrollTop = readSavedProjectsScrollTop()

const persistProjectsScrollTop = (value: number) => {
  savedProjectsScrollTop = value
  try {
    window.sessionStorage.setItem(PROJECTS_SCROLL_STORAGE_KEY, String(value))
  } catch {
    // Ignore storage failures (e.g. privacy mode) - in-memory value still works for this session.
  }
}

const getChatActivityTime = (chat: any): number => {
  const activityDate = chat?.lastModifiedAt || chat?.createdAt
  const activityTime = activityDate ? new Date(activityDate).getTime() : 0
  return Number.isNaN(activityTime) ? 0 : activityTime
}

const sortChatsLatestFirst = (chats: any[]): any[] =>
  [...chats].sort((first, second) => getChatActivityTime(second) - getChatActivityTime(first))

const mergeChatItemsLatestFirst = (existingChats: any[], incomingChats: any[]): any[] => {
  const incomingIds = new Set(incomingChats.map((chat) => chat.chatId))
  const existingWithoutIncoming = existingChats.filter((chat) => !incomingIds.has(chat.chatId))
  return sortChatsLatestFirst([...existingWithoutIncoming, ...incomingChats])
}

const getFlatChatItems = (threads: any): any[] =>
  sortChatsLatestFirst(
    Array.isArray(threads)
      ? threads.map((thread: any) => ({ ...thread, chatId: thread?.id, title: thread?.name }))
      : []
  )

interface ChatHistoryProps {
  showHistory: boolean
  setShowHistory: (chatHistory: boolean) => void
  onNewConversationStart: (persona?: any) => void
  isAliOpen?: boolean
}

export const ChatHistory: React.FC<ChatHistoryProps> = ({
  showHistory,
  setShowHistory,
  onNewConversationStart,
  isAliOpen
}) => {
  const {
    setRawMessages,
    setMessages,
    setSelectedMessage,
    currentChat,
    setCurrentChat,
    setFetchingMsgs,
    isStreaming,
    assistants,
    setDataProductsInChat,
    setThreadMcpServers,
    setThreadPersonalDataBoxes,
    setThreadType,
    selectedProject,
    setSelectedProject,
    projectsRefreshKey,
    projectThreadIdsRef,
    setIsWebSearchEnabled
  } = useContext(ChatbotContext)
  const { appendToken } = useSasToken()
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false)
  const [projectToDelete, setProjectToDelete] = useState<{
    id: string
    name: string
    memoryMode: string | number | undefined
  } | null>(null)
  const [projectToEdit, setProjectToEdit] = useState<ProjectFormValue | null>(null)
  const [isProjectsExpanded, setIsProjectsExpanded] = useState(false)
  const [isAgentsExpanded, setIsAgentsExpanded] = useState(true)
  const [isChatsExpanded, setIsChatsExpanded] = useState(true)
  const projectsListRef = useRef<HTMLDivElement | null>(null)
  // Maps project id -> its row DOM node, so we can scroll the currently selected project's
  // row into view (see the effect below) regardless of where it sits in a long list.
  const projectItemRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const [selectedChatId, setSelectedChatId] = useState<string>('')
  const [chatTitle, setChatTitle] = useState('')
  const [chats, setChats] = useState<any[]>([])
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const { teamId } = useParams<{ teamId: string }>()
  const { value: centrali3_0, isLoading: isCentrali3_0Loading } = useFeatureGate(
    'centrali3_0',
    false
  )

  const sanitizeLoadedMessages = (messages: ChatRawMessageItem[]) =>
    messages.filter((msg) => {
      const hasTempIds = isTempMessageId(msg.id || null) || isTempMessageId(msg.parentId || null)
      if (hasTempIds) return false
      if (msg.type === 'loader') return false
      if (isAbortedContent(msg.content)) return false
      return true
    })

  const {
    deleteChat,
    isFetchingChatHistory,
    getChatHistory,
    cancelAPICall,
    updateChatTitle,
    isDeletingChat,
    getChatMessages,
    getConversationName,
    getThreadDetails
  } = useChatService()
  const { getAgentDetails } = useAgentService()
  const {
    createProject,
    isCreatingProject,
    projects,
    isLoadingProjects,
    refetchProjects,
    deleteProject,
    isDeletingProject,
    getProjectById,
    updateProject,
    isUpdatingProject,
    getProjectThreads,
    removeThreadFromProject
  } = useProjectService()
  const { mcpServers, selectedAgent } = useSelector((state: any) => state.chatbotReducer)
  const assistantList = useSelector((state: any) => state.agentReducer.agentsList)

  //@ts-ignore
  const trackNewChat = useTrackEvent(appInsights, EventType.NewChatButtonClicked)
  //@ts-ignore
  const trackChatHistoryClosed = useTrackEvent(appInsights, EventType.ChatHistoryClicked)
  const { getApplication } = useAppsService()
  const [hasAccess, setHasAccess] = useState<boolean | null>(null)
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null)
  const listContainerRef = useRef<HTMLDivElement | null>(null)
  const [height, setHeight] = useState(400)
  const isDrawerMode = checkIsDrawerMode()
  const errorToastShown = useRef(false)
  const internallySelectedChatIdRef = useRef<string | null>(null)
  const dispatch = useDispatch()
  const { chatId, projectId } = useParams<{ chatId?: string; projectId?: string }>()
  const navigate = useNavigate()
  const aliIcon = `${staticAssetsPath}/images/logo/ALI_Chatbot-vb.png`

  // Declared here (before the effects/handlers below that call it) so it isn't accessed
  // before its declaration - its own closures only depend on things already defined above.
  const getDataSourcesForChat = async (chatId: string) => {
    // this function extracts ALL data sources for a chat:
    // - Databoxes/Data Products (SmartInsights/Snowflake) → setThreadMcpServers (raw array, parsed
    //   by ChatInput.tsx's threadSources memo, mirroring the agentSources memo)
    // - Web Search configuration → setIsWebSearchEnabled
    const threadDetails = await getThreadDetails(chatId)
    /* c8 ignore next */
    const threadPayload = threadDetails?.thread || threadDetails
    setThreadType(threadPayload?.metadata?.Type || null)
    const threadMcpServers =
      threadPayload?.threadMcpServers || threadPayload?.forwardedProps?.threadMcpServers
    // Personal Databox scope is a separate top-level field on the thread
    const threadPersonalDataBoxes =
      threadPayload?.personalDataBoxes || threadPayload?.forwardedProps?.personalDataBoxes
    const toolConfiguration =
      threadPayload?.toolConfiguration ||
      threadPayload?.forwardedProps?.toolConfiguration ||
      threadDetails?.toolConfiguration ||
      threadDetails?.forwardedProps?.toolConfiguration

    const webSearchServer = mcpServers?.find(
      (server: any) => server?.name === MCP_SERVER_NAMES.WEB_SEARCH
    )
    const webSearchServerId = webSearchServer?.id || webSearchServer?.mcpServerId

    // Helper function to check if a tool is web search
    const isWebSearchTool = (tool: any): boolean => {
      /* c8 ignore start */
      if (!tool) return false
      const toolName = `${tool?.toolName || ''}`.toLowerCase()
      return (
        tool?.mcpServerId === webSearchServerId ||
        toolName.includes('websearch') ||
        toolName.includes('web_search') ||
        (toolName.includes('web') && toolName.includes('search'))
      )
      /* c8 ignore stop */
    }

    // Check if web search is enabled in tool configuration
    const configMode = `${toolConfiguration?.mode || ''}`.toLowerCase()
    let isWebSearchEnabledLocal = false

    // 1. Check FORCE_SPECIFIC mode with requiredTool (singular)
    if (configMode === ToolConfigMode.FORCE_SPECIFIC.toLowerCase()) {
      isWebSearchEnabledLocal = isWebSearchTool(toolConfiguration?.requiredTool)
    }
    // 2. Check REQUIRED_ANY mode with allowedTools (array)
    if (configMode === ToolConfigMode.REQUIRED_ANY.toLowerCase()) {
      const allowedTools = toolConfiguration?.allowedTools
      /* c8 ignore start */
      if (Array.isArray(allowedTools) && allowedTools.length > 0) {
        isWebSearchEnabledLocal = allowedTools.some((tool: any) => isWebSearchTool(tool))
      }
      /* c8 ignore stop */
    }

    // null (not false) lets ChatInput derive it from this thread's threadMcpServers; toolConfiguration
    // only decides it for legacy threads, since new requests always save AUTO.
    setIsWebSearchEnabled(isWebSearchEnabledLocal ? true : null)

    // Store the raw threadMcpServers array - mirrors how selectedAgent.agentMcpServers is stored
    // as-is and parsed later by ChatInput.tsx's agentSources memo. ChatInput.tsx's threadSources
    // memo parses THIS the same way (extractOriginalDataSourceIds + cognitiveSearchId extraction),
    // instead of pre-parsing into separate flat fields here.
    setThreadMcpServers(threadMcpServers || [])
    setThreadPersonalDataBoxes(threadPersonalDataBoxes || [])

    // Still needed here (independent of the threadSources memo) for the marketplace-mode
    // dataProductsInChat consumers.
    const { allDataProductIds: allThreadDataProductIds } = extractOriginalDataSourceIds(
      threadMcpServers || null
    )
    setDataProductsInChat([...new Set(allThreadDataProductIds)])
  }

  const loadChatsById = async (chatId: string, pageNo: number) => {
    getDataSourcesForChat(chatId)
    const response: any = await getChatMessages(pageNo, CHAT_MSG_PER_PAGE, chatId)
    if (Array.isArray(response?.messages)) {
      //setTotalMsgCount(response.totalCount)

      const sortedMessages: ChatRawMessageItem[] = [...response.messages]
        .map((msg: ChatRawMessageItem) => {
          const { content, citations, aborted, toolCalls, reasoningContent, reasonings } =
            getMessageContent(msg.content)
          const newMsg = {
            ...msg,
            content,
            citations: citations || [],
            aborted: aborted === true,
            toolCalls: toolCalls || msg.toolCalls || [],
            reasoningContent: reasoningContent || msg.reasoningContent || '',
            reasonings: reasonings || msg.reasonings || []
          }
          return newMsg.parentId === undefined
            ? {
                ...newMsg,
                parentId: null
              }
            : newMsg
        })
        .sort(
          (a: ChatRawMessageItem, b: ChatRawMessageItem) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        )

      const normalizedMessages = normalizeInterruptedBranches(
        sanitizeLoadedMessages(sortedMessages)
      )

      const selectedAgent = assistants?.find(
        (agent: any) => agent.id === sortedMessages[0]?.agentId
      )

      if (selectedAgent) {
        // If the selected agent has the specific Centrali ID, ensure it has the correct avatar URL
        const agentToDispatch =
          String(selectedAgent.id) === 'd2d12f41-449c-4245-93f1-4242c9e4eb04'
            ? {
                ...selectedAgent,
                avatarUrl: appendToken(`${staticAssetsPath}/images/logo/ALI_Chatbot-vb.png`)
              }
            : selectedAgent
        dispatch(setSelectedAgent(agentToDispatch))
      } else {
        const chatAgent = sortedMessages[0]?.agentId
        let agentDetails = undefined
        if (chatAgent) {
          try {
            const { agent } = await getAgentDetails(chatAgent)
            /* c8 ignore next */
            if (agent && agent?.name && agent?.id) agentDetails = agent
          } catch (error) {
            console.error('Failed to fetch agent details:', error)
          }
        }

        // If agentDetails has the specific Centrali ID, ensure it has the correct avatar URL
        /* c8 ignore start */
        if (agentDetails && String(agentDetails.id) === 'd2d12f41-449c-4245-93f1-4242c9e4eb04') {
          agentDetails = {
            ...agentDetails,
            avatarUrl: appendToken(`${staticAssetsPath}/images/logo/ALI_Chatbot-vb.png`)
          }
        }
        /* c8 ignore stop */

        dispatch(
          setSelectedAgent(
            agentDetails || {
              id: 'd2d12f41-449c-4245-93f1-4242c9e4eb04',
              name: 'Centrali',
              avatarUrl: appendToken(`${staticAssetsPath}/images/logo/ALI_Chatbot-vb.png`)
            }
          )
        )
      }

      setRawMessages(normalizedMessages)

      setTimeout(() => setFetchingMsgs(false), 100)
      errorToastShown.current = false
    } else {
      setFetchingMsgs(false)
      setCurrentChat({ id: null, title: null })
      /* c8 ignore next */
      if (!errorToastShown.current) {
        toast.error('Chat not found!')
        errorToastShown.current = true
      }
      navigate('/chat')
    }
  }

  // Stable measurer (functional setState) so it never needs `height` as a dependency —
  // callers (ResizeObserver + the collapse/expand effect below) can safely call it without
  // forcing the observing effect to tear down/recreate on every height change.
  const calculateHeight = useCallback(() => {
    if (!listContainerRef.current) return
    const containerHeight = listContainerRef.current.offsetHeight
    // Only update if height changed by more than 5px to avoid unnecessary re-renders
    setHeight((prevHeight) =>
      containerHeight > 0 && Math.abs(containerHeight - prevHeight) > 5
        ? containerHeight
        : prevHeight
    )
  }, [])

  useEffect(() => {
    if (!showHistory || !listContainerRef.current) return

    const rafId = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(calculateHeight)
    })

    let observer: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined' && listContainerRef.current) {
      observer = new ResizeObserver(calculateHeight)
      observer.observe(listContainerRef.current)
    }

    return () => {
      window.cancelAnimationFrame(rafId)
      observer?.disconnect()
    }
    // isChatsExpanded is included because listContainerRef's node only exists while the
    // Chats section is expanded - re-run so a freshly (re)mounted node gets observed too.
  }, [showHistory, isChatsExpanded, calculateHeight])

  // Sibling sections (Agents/Projects) collapsing or expanding resizes the flex-1 chats
  // list container without necessarily changing showHistory/isChatsExpanded, so the
  // ResizeObserver above may lag or (if its node was swapped out) miss it entirely.
  // useLayoutEffect (not useEffect) so this recompute happens before paint, avoiding a
  // one-frame flash of the list at its old height.
  useLayoutEffect(() => {
    calculateHeight()
  }, [
    isProjectsExpanded,
    isAgentsExpanded,
    projects?.length,
    isLoadingProjects,
    assistantList?.length,
    calculateHeight
  ])

  // Restore the sidebar's project-list scroll position whenever the list (re)appears —
  // on mount (e.g. after a route-driven remount of this component, or a full page reload),
  // when the "Projects" section is expanded again after being collapsed, and after a projects
  // refetch (useProjectService's own mount effect, or triggerProjectsRefresh — e.g. once a
  // response moves a thread into a project) finishes loading.
  //
  // IMPORTANT: `refetchProjects` (projectService.ts) calls `setLoader(false)` (inside
  // useFetch's fetchData) and `setProjects(list)` from two DIFFERENT ticks - they're split
  // across an `await`, so React commits them as two separate renders: one where
  // isLoadingProjects flips true -> false while `projects` is still the old/empty array, and
  // a later one where `projects` actually updates. Depending only on isLoadingProjects means
  // this effect fires too early (list still empty, scrollTop clamps to 0) and never re-fires
  // once the real data arrives. Depending on `projects` too ensures we re-apply the saved
  // scroll position once the list actually has the content to scroll to.
  useEffect(() => {
    if (isProjectsExpanded && !isLoadingProjects && projectsListRef.current) {
      projectsListRef.current.scrollTop = savedProjectsScrollTop
    }
  }, [isProjectsExpanded, isLoadingProjects, projects])

  // Ensure the currently selected project's row is visible in the sidebar's projects list —
  // e.g. opening a project via a direct URL/deep-link/page reload (or the header's active
  // project chip) restores `selectedProject` from the URL param, but that row can sit further
  // down a long project list than wherever the (possibly stale/default) saved scroll position
  // happens to be, leaving the selection out of view above/below the visible area. Runs after
  // the scroll-restore effect above so it takes priority whenever a project is selected.
  useEffect(() => {
    if (!isProjectsExpanded || !selectedProject?.id) return
    const node = projectItemRefs.current.get(selectedProject.id)
    if (!node) return
    node.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [selectedProject?.id, isProjectsExpanded, projects])

  // Refresh the projects list whenever another component (e.g. ProjectView delete) asks for it.
  // Also rebuild the flat history list so a thread removed from a project reappears under
  // regular history (and a deleted thread drops out of it).
  useEffect(() => {
    if (projectsRefreshKey === 0) return
    /* c8 ignore next */
    if (!hasAccess) return
    void refetchProjects()
    const reloadHistory = async () => {
      const { threads } = await getChatHistory(0, 50)
      /* c8 ignore start */
      const nonProjectThreads = Array.isArray(threads)
        ? threads.filter((thread: any) => !thread?.projectId)
        : threads
      setHasMore(nonProjectThreads?.length > 0 || false)
      /* c8 ignore stop */
      setChats(getFlatChatItems(nonProjectThreads))
      setOffset(0)
    }
    void reloadHistory()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsRefreshKey])

  useEffect(() => {
    const fetchApplication = async () => {
      // Fetch Smart-Docs application to check if the user has access to the ARC role "Corp - App - DocAI - GeneralUser"
      const app = await getApplication('Smart-Docs')
      /* c8 ignore next */
      if (app) {
        setHasAccess(!(app?.data?.arc?.isInProcess || app?.data?.arc?.arcRoles?.length > 0))
      }
    }
    fetchApplication()
  }, [])

  useEffect(() => {
    const getAllChats = async () => {
      const { threads } = await getChatHistory(offset, 50)

      // Threads assigned to a project live under the project, not the flat history list.
      /* c8 ignore next 3 */
      const nonProjectThreads = Array.isArray(threads)
        ? threads.filter((thread: any) => !thread?.projectId)
        : threads

      setHasMore(nonProjectThreads?.length > 0 || false)

      // reinitialized grouped chats
      const nextChats = getFlatChatItems(nonProjectThreads)
      setChats((prevChats) =>
        offset === 0 ? nextChats : mergeChatItemsLatestFirst(prevChats, nextChats)
      )
    }

    //Get Centrali's source to map the chats
    if (hasAccess) getAllChats()
  }, [offset, hasAccess])

  // Fetch chat messages if chatId is present in URL
  useEffect(() => {
    //avoid api call when starting a new chat
    if (chatId && !currentChat?.recentlyUpdated) {
      setFetchingMsgs(true)
      // Distinguish sidebar chat clicks (already primed in local state) from external route changes.
      const isInternalSelection = internallySelectedChatIdRef.current === chatId
      const hasRouteChatChanged = currentChat?.id !== chatId

      /* c8 ignore start */
      if (isInternalSelection) {
        internallySelectedChatIdRef.current = null
      } else if (hasRouteChatChanged) {
        setRawMessages([])
        setSelectedMessage(null)
        setCurrentChat((prevChat: any) => ({
          ...prevChat,
          id: chatId,
          title: null
        }))
      }
      /* c8 ignore stop */

      const fetchConversation = async () => {
        /* c8 ignore next */
        if (!isInternalSelection && (hasRouteChatChanged || !currentChat?.title)) {
          const response = await getConversationName(chatId)
          /* c8 ignore next */
          if (response?.thread) {
            setCurrentChat((prevChat: any) => ({
              ...prevChat,
              id: chatId,
              title: response?.thread?.name
            }))
          }
        }

        loadChatsById(chatId, 1)
      }
      fetchConversation()
    }
  }, [chatId])

  // Restore the selected project when a /chat/project/:projectId URL is opened directly or refreshed
  useEffect(() => {
    if (!projectId || isDrawerMode) return
    const match = projects?.find((project: any) => project.id === projectId)
    if (!match) return
    const projectMeta = {
      id: match.id,
      name: match.name,
      icon: match.icon,
      color: match.color
    }
    // /chat/project/:projectId/:chatId — viewing a conversation that belongs to a project.
    // Keep the conversation visible (don't open the project landing) but record the
    // thread→project association so the header chip and breadcrumb show the project.
    if (chatId) {
      projectThreadIdsRef.current.set(chatId, projectMeta)
      return
    }
    /* c8 ignore next */
    if (selectedProject?.id === projectId) return
    setSelectedProject(projectMeta)
  }, [projectId, chatId, projects])

  useEffect(() => {
    const getmessages = () => {
      if (isAliOpen && isDrawerMode && currentChat?.id) {
        setFetchingMsgs(true)
        setRawMessages([])
        setSelectedMessage(null)
        loadChatsById(currentChat?.id, 1)
      }
    }
    getmessages()
  }, [isAliOpen])

  //add newly initiated chat to the history
  useEffect(() => {
    const chatId = currentChat?.id
    const title = currentChat?.title || 'New chat'
    const agentId = (currentChat as any)?.agentId || null
    // Chats started from within a project live under the project, not the flat history list.
    /* c8 ignore start */
    if (chatId && projectThreadIdsRef.current.has(chatId)) {
      const ownerProject = projectThreadIdsRef.current.get(chatId)
      if (!isDrawerMode) {
        if (ownerProject?.id) {
          // Update the address bar only — do NOT use react-router's navigate() here.
          // Router.jsx registers /chat/project/:projectId and /chat/project/:projectId/:chatId
          // as two separate top-level routes, so navigate() crossing between them fully
          // unmounts/remounts the whole page right as the response streams in (this is the
          // "screen reloads and the project-list scroll jumps to top" issue). The UI for this
          // transition is already fully driven by context (selectedProject/currentChat), not
          // by the URL params, so a silent history update is enough to keep the URL correct
          // for a manual refresh or deep-link, without triggering react-router's route match.
          const newPath = `/chat/project/${ownerProject.id}/${chatId}`
          if (window.location.pathname !== newPath) {
            window.history.pushState(null, '', newPath)
          }
        } else {
          navigate(`/chat/${chatId}`)
        }
      }
      return
    }
    /* c8 ignore stop */
    if (chatId && currentChat?.title) {
      // /chat and /chat/:chatId are separate routes, so navigate() here would remount
      // <Chatbot> and wipe ChatbotProvider's context - sync the URL silently instead (only
      // while actually on the chat route, since this component is also mounted standalone).
      if (
        !isDrawerMode &&
        window.location.pathname.startsWith('/chat') &&
        window.location.pathname !== `/chat/${chatId}`
      ) {
        window.history.pushState(null, '', `/chat/${chatId}`)
      }
      const newChat = chats?.find((chat: any) => chat?.chatId === chatId)

      if (!newChat || newChat.title === 'New chat') {
        setChats((prevChats) => {
          const newChatIndex = prevChats.findIndex((item: any) => item?.chatId === chatId)
          const newChatTitle = newChatIndex !== -1 ? prevChats[newChatIndex].title : null

          const newChatItem = {
            chatId,
            title,
            agentId,
            lastModifiedAt: new Date().toISOString()
          }

          /* c8 ignore next */
          if (newChatTitle === null) {
            return sortChatsLatestFirst([newChatItem, ...prevChats])
          } else if (newChatTitle === 'New chat') {
            /* c8 ignore next */
            // Update existing chat with new title
            prevChats[newChatIndex] = { ...prevChats[newChatIndex], title }
            return sortChatsLatestFirst([...prevChats])
          }

          return prevChats // No changes if the title is not null or 'New chat'
        })
      } else if (newChat && newChat.title !== title) {
        // Update the title of the old chat only if the title has changed
        /* c8 ignore start */
        setChats((prevChats) =>
          prevChats.map((chat) => (chat.chatId === chatId ? { ...chat, title } : chat))
        )
        /* c8 ignore stop */
      }
    }
  }, [currentChat])

  // Move the current chat to the top of the list when new messages are received
  useEffect(() => {
    if (currentChat?.id && isStreaming) {
      const chatIdToMove = currentChat.id

      // Find the index of the chat to move
      const currentChatIndex = chats?.findIndex((chat) => chat?.chatId === chatIdToMove)
      if (currentChatIndex === 0) return

      // Find and remove the item with chatId, also get filtered list
      const filteredArr = chats?.filter((chat) => chat?.chatId !== chatIdToMove)
      const itemToMove = chats?.find((chat) => chat?.chatId === chatIdToMove)

      if (!itemToMove) return

      setChats([itemToMove, ...filteredArr])
    }
  }, [isStreaming])

  const loadMoreItems = () => {
    if (hasMore && hasAccess && !isFetchingChatHistory) {
      setOffset((prev) => prev + 50)
    }
  }

  const onDeleteProject = async () => {
    /* c8 ignore start */
    if (!projectToDelete) return

    // Get all threads associated with this project
    const threads = await getProjectThreads(projectToDelete.id)
    const threadIds: string[] = threads?.map((thread: any) => thread.id) || []

    // Remove each thread from the project (backend)
    if (threadIds.length > 0) {
      await Promise.all(
        threadIds.map((threadId: string) => removeThreadFromProject(projectToDelete.id, threadId))
      )
    }

    // Clear frontend project reference from all associated chat threads
    if (projectThreadIdsRef?.current) {
      threadIds.forEach((threadId: string) => {
        projectThreadIdsRef.current?.delete(threadId)
      })
    }

    // If currently viewing a chat that belonged to this project, redirect to non-project URL
    if (currentChat?.id && threadIds.includes(currentChat.id)) {
      if (!isDrawerMode) {
        navigate(`/chat/${currentChat.id}`)
      }
    }

    const deleted = await deleteProject(projectToDelete.id)
    if (deleted) {
      // If the project being deleted is the one open in the main view, clear the
      // selection so the chat/agents view is shown instead of a stale project pane.
      if (selectedProject?.id === projectToDelete.id) setSelectedProject(null)
      await refetchProjects()

      // Refresh the chat history to show the unlinked threads in normal conversations
      const { threads } = await getChatHistory(0, 50)
      const nonProjectThreads = Array.isArray(threads)
        ? threads.filter((thread: any) => !thread?.projectId)
        : threads
      setHasMore(nonProjectThreads?.length > 0 || false)
      setChats(getFlatChatItems(nonProjectThreads))
      setOffset(0)

      setProjectToDelete(null)
    }
    /* c8 ignore stop */
  }

  const onEditProject = (project: { id: string; name: string; icon: string; color: string }) => {
    // Seed the edit modal with the basic details we already have, then enrich with
    // full details (instructions + memory mode) once they load.
    setProjectToEdit(project as ProjectFormValue)
    /* c8 ignore start */
    getProjectById(project.id).then((full) => {
      if (full) {
        setProjectToEdit((prev) => (prev?.id === full.id ? (full as ProjectFormValue) : prev))
      }
    })
    /* c8 ignore stop */
  }

  const onChatSelection = async (chatId: string, title: string) => {
    if (chatId === currentChat?.id) return
    setSelectedProject(null)
    setFetchingMsgs(true)
    setRawMessages([])
    void cancelAPICall(false)
    setSelectedMessage(null)
    internallySelectedChatIdRef.current = isDrawerMode ? null : chatId
    setCurrentChat({ id: chatId, title })
    if (isDrawerMode) {
      loadChatsById(chatId, 1)
    } else {
      navigate(`/chat/${chatId}`)
    }
  }

  const onDelete = async () => {
    const response = await deleteChat(selectedChatId)
    if (response) {
      toast.success('Chat deleted successfully')
    } else {
      toast.error('Failed to delete chat')
    }
    setIsDeleteModalOpen(!isDeleteModalOpen)
    setChats((prevChats) => prevChats.filter((chat: any) => chat?.chatId !== selectedChatId))
    onNewConversationStart()
  }

  const toggleRenameModal = useCallback(() => {
    setIsRenameModalOpen((prevState) => !prevState)
  }, [])

  const handleRenameClick = (chatId: string, title: string) => {
    setSelectedChatId(chatId)
    setChatTitle(title)
    toggleRenameModal()
  }

  const onRenameChat = async () => {
    updateChatTitle(selectedChatId, chatTitle)
    toast.success('Chat title updated successfully')

    // Update current chat if it matches the selected chat
    if (currentChat?.id === selectedChatId) {
      setCurrentChat((prevChat: any) => ({
        ...prevChat,
        title: chatTitle
      }))
    }

    // Update the chats list with the new title
    setChats((prevChats: any) =>
      prevChats.map((chat: any) => {
        return chat.chatId === selectedChatId ? { ...chat, title: chatTitle } : chat
      })
    )
  }

  const onAgentSelect = (agent: AgentData) => {
    dispatch(setSelectedAgent({ ...agent }))
    // No args - the param is a navigateToChat boolean, not an agent
    onNewConversationStart()
  }

  const getRecentChats = (chats: any[], limit = 20) => {
    const res: any[] = []

    for (const item of chats) {
      if (res.length >= limit) break
      res.push(item)
    }

    return res
  }

  // Render the agents section in the chat sidebar
  const renderAgentSection = () => {
    const topAgents = assistantList.slice(0, 3)
    return (
      <div className="mx-50 my-[4px] flex flex-col mt-2.5" data-testid="agents-section">
        <div className="flex items-center justify-between">
          <div
            className="chat-item theme-interactive-item theme-text-subheading flex flex-1 items-center gap-1 p-50 text-body-default transition-colors duration-150 cursor-pointer rounded-[7px] outline-none justify-content-between"
            onClick={() => setIsAgentsExpanded((prev) => !prev)}
            role="button"
            tabIndex={0}
            aria-expanded={isAgentsExpanded}
            aria-label="Toggle agents list"
            data-testid="agents-toggle">
            <span className="text-gray-500">Agents</span>
            {isAgentsExpanded ? (
              <IconChevronDown size={16} className="flex-shrink-0" />
            ) : (
              <IconChevronRight size={16} className="flex-shrink-0" />
            )}
          </div>
        </div>
        {isAgentsExpanded && (
          <>
            <div
              className="flex flex-col max-h-[192px] overflow-y-auto mt-[2px]"
              data-testid="projects-list">
              {topAgents?.length ?? 0 ? (
                topAgents.map((assistant: any) => {
                  return (
                    <div
                      key={assistant.id}
                      title={assistant.name}
                      aria-current={selectedAgent?.id === assistant.id}
                      className="chat-item hover:bg-[var(--item-hover-bg)] flex items-center gap-1.5 cursor-pointer rounded-[7px]"
                      onClick={() => onAgentSelect(assistant)}
                      onMouseEnter={() => setHoveredItemId(assistant.id)}
                      onMouseLeave={() => setHoveredItemId(null)}
                      data-testid="project-item">
                      <Avatar
                        size={'small'}
                        id="personaName"
                        data-tooltip-id="persona"
                        content={assistant?.avatarUrl || getInitials(assistant?.name)}
                        className="shrink-0 me-50"
                      />
                      <span
                        className={`flex-1 text-body-default theme-interactive-item truncate cursor-pointer ${
                          selectedAgent?.id === assistant.id
                            ? 'theme-selected-text'
                            : 'theme-text-subheading'
                        }`}>
                        {assistant.name}
                      </span>
                      {!!topAgents?.length && (
                        <div
                          className={`transition-opacity duration-200 flex items-center justify-center w-6 h-6 rounded mr-1 ${
                            hoveredItemId === assistant.id ? 'opacity-100' : 'opacity-0'
                          }`}
                          onClick={(e: any) => {
                            e.stopPropagation()
                            navigate(
                              location.pathname.includes('team')
                                ? `/teams/${teamId}/edit-agent/${assistant.id}`
                                : `/agents/edit-agent/${assistant.id}`
                            )
                          }}>
                          <IconEdit size={16} />
                        </div>
                      )}
                    </div>
                  )
                })
              ) : (
                <div className="px-50 py-1 text-table-subtext theme-text-subheading">
                  No agents added yet
                </div>
              )}
            </div>
            {/* More agents button section*/}
            <div className="flex items-center justify-between">
              <div
                className="chat-item theme-interactive-item theme-text-subheading flex flex-1 items-center gap-1 p-50 text-body-default transition-colors duration-150 cursor-pointer rounded-[7px] outline-none"
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/manage-agents`)
                }}
                role="button"
                aria-label="More agents"
                data-testid="more-agents">
                <IconDots size={20} className="theme-icon-muted outline-none" />
                <span className="flex-1">More agents</span>
              </div>
            </div>
            {/* New agent button section */}
            <div className="flex items-center justify-between">
              <div
                className="chat-item theme-interactive-item theme-text-subheading flex flex-1 items-center gap-1 p-50 text-body-default transition-colors duration-150 cursor-pointer rounded-[7px] outline-none"
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/agents/add-agent`)
                }}
                role="button"
                aria-label="New agent"
                data-testid="new-agent">
                <IconSquarePlus size={20} className="theme-icon-muted outline-none" />
                <span className="flex-1">New agent</span>
              </div>
            </div>
          </>
        )}
      </div>
    )
  }

  const renderProjectSection = () => {
    return (
      <div className="mx-50 flex flex-col" data-testid="projects-section">
        <div className="flex items-center justify-between">
          <div
            className="chat-item theme-interactive-item theme-text-subheading flex flex-1 items-center gap-1 p-50 text-body-default transition-colors duration-150 cursor-pointer rounded-[7px] outline-none justify-content-between"
            // className="chat-item theme-interactive-item theme-text-subheading flex flex-1 items-center gap-[5px] p-50 text-body-default transition-colors duration-150 cursor-pointer rounded-[7px] outline-none"
            onClick={() => setIsProjectsExpanded((prev) => !prev)}
            role="button"
            tabIndex={0}
            aria-expanded={isProjectsExpanded}
            aria-label="Toggle projects list"
            data-testid="projects-toggle">
            <span className="text-gray-500">Projects</span>
            {isProjectsExpanded ? (
              <IconChevronDown size={16} className="flex-shrink-0" />
            ) : (
              <IconChevronRight size={16} className="flex-shrink-0" />
            )}
          </div>
        </div>
        {isProjectsExpanded && (
          <>
            <div
              ref={projectsListRef}
              onScroll={(e) => {
                // Ignore scroll events fired while the loading spinner is shown. Swapping
                // the list for the single-row spinner during a refetch shrinks the
                // container's content below its own height, so the BROWSER itself force-
                // resets scrollTop to 0 (native overflow behaviour) - and that native reset
                // fires its own 'scroll' event. Persisting that value here would clobber the
                // real saved position with 0 right before the restore effect re-applies it,
                // which is why the list kept jumping back to the top after every refetch.
                if (isLoadingProjects) return
                persistProjectsScrollTop(e.currentTarget.scrollTop)
              }}
              className="flex flex-col max-h-[192px] overflow-y-auto mt-[2px]"
              data-testid="projects-list">
              {isLoadingProjects ? (
                <div className="flex items-center gap-1 px-50 py-1 text-body-default theme-text-subheading">
                  <Spinner size="sm" color="primary" />
                  <span>Loading projects...</span>
                </div>
              ) : projects?.length ?? 0 ? (
                projects.map((project) => {
                  const ProjectIcon = getProjectIcon(project.icon)
                  return (
                    <div
                      key={project.id}
                      // Standard React "list of refs" pattern (populating a ref Map from a
                      // callback ref) - runs during commit, not render, despite the lint rule.
                      /* eslint-disable react-hooks/refs */
                      ref={(el) => {
                        if (el) projectItemRefs.current.set(project.id, el)
                        else projectItemRefs.current.delete(project.id)
                      }}
                      /* eslint-enable react-hooks/refs */
                      title={project.name}
                      aria-current={selectedProject?.id === project.id}
                      className={`chat-item hover:bg-[var(--item-hover-bg)] flex items-center gap-1 p-50 cursor-pointer rounded-[7px] ${
                        selectedProject?.id === project.id ? 'bg-[var(--item-hover-bg)]' : ''
                      }`}
                      onMouseEnter={() => setHoveredItemId(project.id)}
                      onMouseLeave={() => setHoveredItemId(null)}
                      onClick={() => {
                        setRawMessages([])
                        setMessages([])
                        setCurrentChat({ id: null, title: null })
                        setSelectedMessage(null)
                        setSelectedProject({
                          id: project.id,
                          name: project.name,
                          icon: project.icon,
                          color: project.color
                        })
                        if (!isDrawerMode) navigate(`/chat/project/${project.id}`)
                      }}
                      data-testid="project-item">
                      <ProjectIcon
                        size={20}
                        className="flex-shrink-0"
                        style={{ color: getProjectColour(project.color) }}
                      />
                      <span
                        className={`flex-1 text-body-default theme-interactive-item truncate cursor-pointer ${
                          selectedProject?.id === project.id
                            ? 'theme-selected-text'
                            : 'theme-text-subheading'
                        }`}>
                        {project.name}
                      </span>
                      {!!projects?.length && (
                        <UncontrolledDropdown>
                          <DropdownToggle
                            tag="div"
                            className={`transition-opacity duration-200 flex items-center justify-center w-6 h-6 rounded ${
                              hoveredItemId === project.id ? 'opacity-100' : 'opacity-0'
                            }`}
                            onClick={(e) => e.stopPropagation()}
                            data-testid="project-item-actions">
                            <IconDotsVertical size={16} className="theme-text-subheading" />
                          </DropdownToggle>
                          <DropdownMenuContainer
                            className="text-[14px] !min-w-0 w-max"
                            strategy="fixed"
                            container="body"
                            $isCompact>
                            <DropdownItem
                              className="w-full theme-dropdown-accent-item"
                              onClick={(e: any) => {
                                e.stopPropagation()
                                onEditProject(project)
                              }}>
                              <div className="flex items-center gap-1">
                                <IconEdit size={16} />
                                <span>Edit</span>
                              </div>
                            </DropdownItem>
                            <DropdownItem
                              className="w-full theme-dropdown-danger-item"
                              onClick={(e: any) => {
                                e.stopPropagation()
                                setProjectToDelete({
                                  id: project.id,
                                  name: project.name,
                                  memoryMode: project.memoryMode
                                })
                              }}>
                              <div className="flex items-center gap-1">
                                <IconTrash size={16} />
                                <span>Delete</span>
                              </div>
                            </DropdownItem>
                          </DropdownMenuContainer>
                        </UncontrolledDropdown>
                      )}
                    </div>
                  )
                })
              ) : (
                <div className="chat-item hover:bg-[var(--item-hover-bg)] flex items-center gap-1 p-50 cursor-pointer rounded-[7px]">
                  No projects added yet
                </div>
              )}
            </div>
            <div className="flex items-center justify-between">
              <div
                className="chat-item theme-interactive-item theme-text-subheading flex flex-1 items-center gap-1 p-50 text-body-default transition-colors duration-150 cursor-pointer rounded-[7px] outline-none"
                onClick={(e) => {
                  e.stopPropagation()
                  setIsCreateProjectModalOpen(true)
                }}
                role="button"
                aria-label="New project"
                data-testid="new-project">
                <IconSquarePlus size={20} className="theme-icon-muted outline-none" />
                <span className="flex-1">New project</span>
              </div>
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <div
      id="chat-history"
      className={`theme-frosted-sidebar theme-chat-history-shadow min-h-full top-0 left-0 z-[15] flex flex-col
        ${showHistory ? 'w-[250px]' : 'w-0'}
        ${isDrawerMode ? 'absolute bottom-0' : 'relative'}
        ${
          isDrawerMode && !showHistory
            ? 'opacity-0 pointer-events-none'
            : 'opacity-100 pointer-events-auto'
        }
        transition-[width,opacity,transform] duration-[200ms] ease-[cubic-bezier(0.25,0.8,0.25,1)] chat-history chat-primary`}
      onMouseLeave={() => {
        /* c8 ignore next */
        if (isDrawerMode) setShowHistory(false)
      }}>
      {showHistory && (
        <>
          <div className="flex pt-50 items-center justify-between">
            <div
              className="chat-item theme-interactive-item theme-text-subheading flex items-center gap-1 flex-1 mx-50 p-50 text-body-default transition-colors duration-150 cursor-pointer rounded-[7px]"
              onClick={() => {
                trackNewChat({ location: 'Sidebar' })
                setSelectedProject(null)
                onNewConversationStart()
              }}
              data-testid="new-chat-button">
              <IconSquarePlus size={20} className="flex-shrink-0 theme-icon-muted outline-none" />
              <span>New chat</span>
            </div>
            {!isDrawerMode && (
              <div className="flex justify-end p-50 me-50 theme-hover-surface rounded-full transition-colors duration-200 cursor-pointer outline-none">
                <IconLayoutSidebarLeftCollapse
                  className="outline-none theme-icon-muted"
                  size={20}
                  data-testid="closeSidebar"
                  data-tooltip-id="closeSidebar"
                  onClick={() => {
                    trackChatHistoryClosed({ chatHistory: 'Closed' })
                    // Delay state update to ensure tracking event is sent
                    setTimeout(() => setShowHistory(false), 100)
                  }}
                />
                <Tooltip
                  id="closeSidebar"
                  place="bottom"
                  className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
                  Close sidebar
                </Tooltip>
              </div>
            )}
          </div>
          <div
            className="chat-item theme-interactive-item theme-text-subheading flex items-center gap-1 mx-50 p-50 my-[4px] text-body-default transition-colors duration-150 cursor-pointer rounded-[7px]"
            onClick={() => setIsSearchModalOpen(true)}
            data-testid="search-chats-button">
            <IconSearch size={20} className="flex-shrink-0 theme-icon-muted outline-none" />
            <span>Search chats</span>
          </div>
          {/* Projects section: collapsible list with more projects and create-new project option */}
          {centrali3_0 && renderProjectSection()}
          {/* Agents section: collapsible list with more agents and create-new agent option */}
          {renderAgentSection()}
          {/* Chats section header: collapsible toggle for the whole date-grouped chat list */}
          <div
            className="chat-item theme-interactive-item theme-text-subheading flex items-center gap-1 mx-50 mt-2.5 p-50 text-body-default transition-colors duration-150 cursor-pointer rounded-[7px] outline-none"
            onClick={() => setIsChatsExpanded((prev) => !prev)}
            role="button"
            tabIndex={0}
            aria-expanded={isChatsExpanded}
            aria-label="Toggle chats list"
            data-testid="chats-toggle">
            <span className="flex-1 text-gray-500">Chats</span>
            {isChatsExpanded ? (
              <IconChevronDown size={16} className="flex-shrink-0" />
            ) : (
              <IconChevronRight size={16} className="flex-shrink-0" />
            )}
          </div>
          {isChatsExpanded && (
            <div className="flex-1 overflow-hidden theme-frosted-surface" ref={listContainerRef}>
              {hasAccess && chats?.length ? (
                //@ts-ignore
                <InfiniteLoader
                  isItemLoaded={({ index }: any) => !!chats[index]}
                  loadMoreItems={loadMoreItems}
                  itemCount={hasMore ? chats.length + 1 : chats.length}>
                  {({ onItemsRendered, ref }: any) => (
                    <>
                      {/* @ts-ignore */}
                      <List
                        height={height}
                        width="100%"
                        itemCount={chats.length}
                        itemSize={40}
                        onItemsRendered={onItemsRendered}
                        ref={ref}>
                        {({ index, style }: any) => {
                          const item: any = chats[index]

                          return (
                            <div
                              key={item?.chatId}
                              style={{ ...style, width: '-webkit-fill-available' }}
                              className={`chat-item hover:bg-[var(--item-hover-bg)] mx-50 !pl-4 flex justify-between items-center cursor-pointer p-50 rounded-[7px] ${
                                item?.chatId === currentChat?.id || hoveredItemId === item?.chatId
                                  ? 'bg-[var(--item-hover-bg)]'
                                  : ''
                              }`}
                              onMouseEnter={() => setHoveredItemId(item?.chatId)}
                              onMouseLeave={() => setHoveredItemId(null)}>
                              <div
                                title={
                                  /* c8 ignore next */
                                  item?.title || 'New chat'
                                }
                                className={`flex-1 text-body-default theme-interactive-item truncate cursor-pointer ${
                                  item?.chatId === currentChat?.id
                                    ? 'theme-selected-text'
                                    : 'theme-text-subheading'
                                }`}
                                onClick={(e: any) => {
                                  e.preventDefault()
                                  onChatSelection(item?.chatId, item?.title)
                                }}>
                                {
                                  /* c8 ignore next */
                                  item?.title || 'New chat'
                                }
                              </div>
                              <UncontrolledDropdown>
                                <DropdownToggle
                                  tag="div"
                                  className={`transition-opacity duration-200 flex items-center justify-center w-6 h-6 ml-50 rounded ${
                                    hoveredItemId === item?.chatId ? 'opacity-100' : 'opacity-0'
                                  }`}
                                  onClick={(e) => e.stopPropagation()}>
                                  <IconDotsVertical size={16} className="theme-text-subheading" />
                                </DropdownToggle>
                                <DropdownMenuContainer
                                  className="dropdown-menu-container !min-w-0 w-max"
                                  strategy="fixed"
                                  container="body"
                                  $isCompact>
                                  <DropdownItem
                                    className="w-full theme-dropdown-accent-item"
                                    onClick={() => handleRenameClick(item?.chatId, item?.title)}>
                                    <div className="flex items-center gap-1">
                                      <IconEdit size={16} />
                                      <span>Rename</span>
                                    </div>
                                  </DropdownItem>
                                  <DropdownItem
                                    className="w-full theme-dropdown-danger-item"
                                    onClick={() => {
                                      setChatTitle(item?.title)
                                      setIsDeleteModalOpen(!isDeleteModalOpen)
                                      setSelectedChatId(item?.chatId)
                                    }}>
                                    <div className="flex items-center gap-1">
                                      <IconTrash size={16} />
                                      <span>Delete</span>
                                    </div>
                                  </DropdownItem>
                                </DropdownMenuContainer>
                              </UncontrolledDropdown>
                            </div>
                          )
                        }}
                      </List>
                    </>
                  )}
                </InfiniteLoader>
              ) : (
                <div className="flex h-3/4 w-full justify-center items-center p-2 text-center absolute">
                  {hasAccess === null || isFetchingChatHistory ? (
                    <div className="flex flex-col items-center space-y-2">
                      <Spinner color="primary" />
                      <span className="text-body-default">loading...</span>
                    </div>
                  ) : hasAccess ? (
                    <p className="text-gray-600">💬 Your recent chats will show up here.</p>
                  ) : (
                    <p className=" text-gray-600">
                      Oops! Seems like you don&apos;t have access to chat history. Please contact
                      support via the &apos;Need Help?&apos; button on the Centrali Portal.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {isSearchModalOpen && (
        <ChatSearchModal
          isOpen={isSearchModalOpen}
          toggle={() => setIsSearchModalOpen(false)}
          onSelectChat={(chatId, title) => onChatSelection(chatId, title)}
          recentChats={getRecentChats(chats)}
        />
      )}
      {isRenameModalOpen && (
        <RenameChatModal
          isOpen={isRenameModalOpen}
          toggle={toggleRenameModal}
          chatTitle={chatTitle}
          setChatTitle={setChatTitle}
          onRenameChat={onRenameChat}
        />
      )}
      {isDeleteModalOpen && (
        <DeleteChatModal
          isOpen={isDeleteModalOpen}
          toggle={() => setIsDeleteModalOpen(!isDeleteModalOpen)}
          onDelete={onDelete}
          chatTitle={chatTitle}
          isDeletingChat={isDeletingChat}
        />
      )}
      {
        /* c8 ignore next */
        isCreateProjectModalOpen && (
          <ProjectFormModal
            isOpen={isCreateProjectModalOpen}
            toggle={() => setIsCreateProjectModalOpen(false)}
            onSubmit={async (payload) => {
              const created = await createProject(payload)
              await refetchProjects()
              setIsProjectsExpanded(true)
              setIsCreateProjectModalOpen(false)
              navigate(`/chat/project/${created?.id}`)
            }}
            isSubmitting={isCreatingProject}
          />
        )
      }
      {
        /* c8 ignore next */
        projectToDelete && (
          <DeleteProjectModal
            isOpen={!!projectToDelete}
            toggle={() => setProjectToDelete(null)}
            onDelete={onDeleteProject}
            project={projectToDelete}
            isDeleting={isDeletingProject}
          />
        )
      }
      {
        /* c8 ignore next */
        projectToEdit && (
          <ProjectFormModal
            isOpen={!!projectToEdit}
            toggle={() => setProjectToEdit(null)}
            project={projectToEdit}
            isSubmitting={isUpdatingProject}
            onSubmit={async (payload) => {
              const updated = await updateProject(projectToEdit.id as string, payload)
              /* c8 ignore start */
              if (updated) {
                // Keep the open project view in sync if it's the one being edited
                if (selectedProject && selectedProject.id === projectToEdit.id) {
                  setSelectedProject({
                    ...selectedProject,
                    name: payload.name,
                    icon: payload.icon,
                    color: payload.color,
                    instructions: payload.instructions,
                    memoryMode: payload.memoryMode
                  })
                }
                await refetchProjects()
              }
              /* c8 ignore stop */
              setProjectToEdit(null)
            }}
          />
        )
      }
    </div>
  )
}
